import { useMemo, useState } from "react";
import { Row, Col, Nav } from "react-bootstrap";
import { useDates } from "dates/DatesContext";
import useSelectedMetrics from "components/custom/merchandise/hooks/useSelectedMetrics";
import MultiMetricChart from "components/custom/merchandise/MultiMetricChart";
import Loading from "components/core/blocks/Loading";
import DetailedMetric from "components/core/blocks/DetailedMetric";
import { buildDSPMetric } from "./metrics";
import { useAppSelector } from "redux/hooks";
import useDSPAudienceData from "./useDSPAudienceData";

const AudienceExpander = ({ row }) => {
  const [tab, setTab] = useState("overview");
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const { group } = useDates();

  const { segment_id: segmentId, segment_name: name, line_item_id: lineItemId } = row._original ?? {};
  const hasImpression = row?.dsp_impressions;

  const {
    data: audienceData,
    metadata: audienceMetadata,
    isLoading: isAudienceDataLoading,
  } = useDSPAudienceData("segment_id", true, {
    segment_id: segmentId,
    line_item_id: lineItemId,
  });

  const { data: audienceTimeSeriesData, isLoading: isAudienceTimeSeriesDataLoading } = useDSPAudienceData(
    `segment_id,${group}`,
    true,
    { segment_id: segmentId, line_item_id: lineItemId },
  );

  const data = useMemo(() => {
    return {
      date: audienceTimeSeriesData?.map((row) => ({ ...row, start_date: row.date })) ?? [],
      total: audienceData?.[0] ?? {},
      metadata: audienceMetadata?.[0] ?? {},
      isLoading: isAudienceDataLoading,
    };
  }, [audienceData, audienceMetadata, audienceTimeSeriesData, isAudienceDataLoading]);

  const { selectedMetrics, toggleMetric } = useSelectedMetrics(
    ["dsp_sales", "dsp_cost", "dsp_units"],
    "dspLineItemExpander",
  );

  const metrics = useMemo(
    () => [
      buildDSPMetric(marketPlace.marketPlace, data, "dsp_sales", "dsp_sales_percent"),
      buildDSPMetric(marketPlace.marketPlace, data, "dsp_total_sales", "dsp_total_sales_percent"),
      buildDSPMetric(marketPlace.marketPlace, data, "dsp_impressions", "dsp_impressions_percent"),
      buildDSPMetric(marketPlace.marketPlace, data, "dsp_clicks", "dsp_clicks_percent"),
      buildDSPMetric(marketPlace.marketPlace, data, "dsp_cost", "dsp_cost_percent"),
      buildDSPMetric(marketPlace.marketPlace, data, "dsp_total_purchases", "dsp_total_purchases_percent"),
    ],
    [marketPlace, data],
  );

  const handleLegendChange = (e) => {
    const id = e.target?.options?.custom?.id;
    if (id) toggleMetric(id);
    return false;
  };

  if (isAudienceDataLoading || isAudienceTimeSeriesDataLoading) {
    return <Loading height={"300px"} fullPage={false} />;
  }

  return (
    <div className="bg-white">
      <Row className="pt-3">
        <Col xs={12} md={6} className="pb-4 px-5">
          <h3
            style={{
              fontSize: 22,
            }}
          >
            {name}
          </h3>
        </Col>
      </Row>
      <Row className="px-3">
        <Col xs={12} className="fs-4 my-3 border-bottom">
          <Nav
            variant="underline"
            onSelect={(key) => {
              return setTab(key);
            }}
            activeKey={tab}
          >
            <Nav.Item>
              <Nav.Link eventKey="overview">Overview</Nav.Link>
            </Nav.Item>
          </Nav>
        </Col>
      </Row>
      <Row>
        {tab === "overview" && (
          <>
            {hasImpression ?
              <>
                <Col xs={12}>
                  <MultiMetricChart
                    metrics={metrics}
                    selectedMetrics={selectedMetrics}
                    data={data?.date ?? []}
                    group={group}
                    handleLegendChange={handleLegendChange}
                    datesFormatted={true}
                    showAnnotations={false}
                  />
                </Col>
                <Col xs={12}>
                  <Row className="pt-4">
                    {metrics?.map((m, i) => {
                      return (
                        <Col xs={12} md={6} lg={2} key={m.id} className={`mb-4 ${i % 2 === 0 ? "ps-2" : "pe-2"}`}>
                          <div className="">
                            <div>
                              <DetailedMetric
                                center
                                size={"small"}
                                id={m.id}
                                toolTip={m.toolTip}
                                name={m.name}
                                value={m.overrideValue ?? m.actual}
                                target={m.target}
                                formatter={m.formatter}
                                targetFormatter={m?.targetFormatter}
                                showTarget={false}
                                targetLabel={m?.targetLabel}
                                targetColor={m.target > 0 ? "#007000" : "#d2222d"}
                              />
                            </div>
                          </div>
                        </Col>
                      );
                    })}
                  </Row>
                </Col>
              </>
            : <Col xs={12} className="text-center pb-5">
                <h4>No Data Available</h4>
                <p>This section will be populated once the audience has at least 1 impression.</p>
              </Col>
            }
          </>
        )}
      </Row>
    </div>
  );
};

export default AudienceExpander;
